import { inject, Service } from '@angular/core';
import { Api } from '@api/api';
import {
  createQuickReview,
  findProducts,
  findProductsByIds,
  getAggregatedProductsProduct,
  getVariants,
  getLastAddedProducts,
  getProductBySlug,
  incViewsCount,
  type FindProducts$Params,
} from '@api/functions';
import type { AddProductQuickReviewDto, PaginatedDtoOfProductListItemDto, ProductListResponse, ProductResponseDto, ProductsSort } from '@api/models';
import { Subject, type Observable } from 'rxjs';
import type { ISelectedFilterIds } from '../components/filters/filter/selected-filter-ids.interface';
import { DEFAULT_PRODUCTS_LIMIT } from '../constants';

@Service()
export class ProductService {
  private api = inject(Api);

  private maxViewedProductsArrLength = 30;
  private viewedProductIdsStorageKey = 'recentlyViewedProducts';
  public needToFetchProducts$ = new Subject<void>();

  fetchProduct(slug: string): Observable<ProductResponseDto> {
    return this.api.invoke(getProductBySlug, { slug });
  }

  fetchProductsByIds(productIds: number[], limit?: number): Observable<PaginatedDtoOfProductListItemDto> {
    return this.api.invoke(findProductsByIds, { body: { ids: productIds, limit: limit } });
  }

  fetchProductsByFilters(
    sort: ProductsSort,
    page: number,
    selectedFilterIds: ISelectedFilterIds[],
    defaultParams: Partial<FindProducts$Params> = {},
  ): Observable<ProductListResponse> {
    const params: FindProducts$Params = {
      ...defaultParams,
      sort: sort,
      page: page,
      limit: DEFAULT_PRODUCTS_LIMIT,
      filters: selectedFilterIds.map(filter => ({
        field: filter.id,
        values: filter.valueIds.map(String),
      })),
    };

    return this.api.invoke(findProducts, params);
  }

  fetchAggregatedProducts(productId: number) {
    return this.api.invoke(getAggregatedProductsProduct, { productId });
  }

  /** Every enabled variant of the product's group, the product itself included, in group order */
  fetchVariants(productId: number): Observable<PaginatedDtoOfProductListItemDto> {
    return this.api.invoke(getVariants, { productId });
  }

  fetchRecentlyAddedProducts(): Observable<PaginatedDtoOfProductListItemDto> {
    return this.api.invoke(getLastAddedProducts);
  }

  addViewedProductIdToLocalStorage(productId: number) {
    let recentlyViewedProducts: number[] = localStorage.getItem(this.viewedProductIdsStorageKey)
      ? JSON.parse(localStorage.getItem(this.viewedProductIdsStorageKey))
      : [];

    if (recentlyViewedProducts.length >= this.maxViewedProductsArrLength) {
      recentlyViewedProducts.pop();
    }

    recentlyViewedProducts.unshift(productId);

    recentlyViewedProducts = [...new Set(recentlyViewedProducts)];
    localStorage.setItem(this.viewedProductIdsStorageKey, JSON.stringify(recentlyViewedProducts));
  }

  getViewedProductsFromLocalStorage() {
    return JSON.parse(localStorage.getItem(this.viewedProductIdsStorageKey));
  }

  addQuickReview(productId: number, rating: number) {
    const dto: AddProductQuickReviewDto = { rating };
    return this.api.invoke(createQuickReview, { productId, body: dto });
  }

  incrementViewsCount(id: number) {
    return this.api.invoke(incViewsCount, { productId: id });
  }
}
